import { styled, keyframes } from '@mui/material/styles';
import { Box } from '@mui/material';

// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

interface StyledMasonryProps {
  columns?: number;
  gap?: number;
  method?: 'css' | 'js';
  transitionDuration?: number;
  animated?: boolean;
  animationType?: 'fade' | 'scale' | 'slideUp' | 'slideDown' | 'fadeScale';
  staggerDelay?: number;
  hoverAnimation?: boolean;
  animationDuration?: number;
  uniformHeight?: boolean;
  maxHeight?: number;
}

export const StyledMasonry = styled(Box, {
  shouldForwardProp: (prop) => 
    !['columns', 'gap', 'method', 'transitionDuration', 'animated', 'animationType', 'staggerDelay', 'hoverAnimation', 'animationDuration', 'uniformHeight', 'maxHeight'].includes(prop as string),
})<StyledMasonryProps>(({ gap = 16 }) => {
  return {
    display: 'flex',
    width: '100%',
    gap: gap,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  };
});

interface MasonryColumnProps {
  gap?: number;
  animated?: boolean;
  animationType?: 'fade' | 'scale' | 'slideUp' | 'slideDown' | 'fadeScale';
  staggerDelay?: number;
  hoverAnimation?: boolean;
  animationDuration?: number;
  columnIndex?: number;
  uniformHeight?: boolean;
  maxHeight?: number;
}

export const MasonryColumn = styled(Box, {
  shouldForwardProp: (prop) => !['gap', 'animated', 'animationType', 'staggerDelay', 'hoverAnimation', 'animationDuration', 'columnIndex', 'uniformHeight', 'maxHeight'].includes(prop as string),
})<MasonryColumnProps>(({ 
  gap = 16, 
  animated = false,
  animationType = 'fadeScale',
  staggerDelay = 100,
  hoverAnimation = false,
  animationDuration = 0.6,
  columnIndex = 0,
  uniformHeight = false,
  maxHeight,
  theme
}) => {
  // Get the correct animation based on type
  const getAnimation = () => {
    switch (animationType) {
      case 'fade':
        return fadeIn;
      case 'scale':
        return scaleIn;
      case 'slideUp':
        return slideUp;
      case 'slideDown':
        return slideDown;
      case 'fadeScale':
      default:
        return fadeScale;
    }
  };

  return {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    gap: gap,
    
    '& > *': {
      width: '100%',
      boxSizing: 'border-box',
      
      // Uniform height styling
      ...(uniformHeight && maxHeight && {
        '& > *': {
          height: `${maxHeight}px`,
          display: 'flex',
          flexDirection: 'column',
        },
      }),
      
      // Animation styles for JS method
      ...(animated && {
        animation: `${getAnimation()} ${animationDuration}s ease-out forwards`,
        animationFillMode: 'both',
        
        // Stagger animation for left-to-right flow
        // Items in the same row (across columns) animate together, then next row
        '&:nth-of-type(1)': { animationDelay: `${(columnIndex + 0 * 10) * staggerDelay}ms` },
        '&:nth-of-type(2)': { animationDelay: `${(columnIndex + 1 * 10) * staggerDelay}ms` },
        '&:nth-of-type(3)': { animationDelay: `${(columnIndex + 2 * 10) * staggerDelay}ms` },
        '&:nth-of-type(4)': { animationDelay: `${(columnIndex + 3 * 10) * staggerDelay}ms` },
        '&:nth-of-type(5)': { animationDelay: `${(columnIndex + 4 * 10) * staggerDelay}ms` },
        '&:nth-of-type(6)': { animationDelay: `${(columnIndex + 5 * 10) * staggerDelay}ms` },
        '&:nth-of-type(7)': { animationDelay: `${(columnIndex + 6 * 10) * staggerDelay}ms` },
        '&:nth-of-type(8)': { animationDelay: `${(columnIndex + 7 * 10) * staggerDelay}ms` },
        '&:nth-of-type(9)': { animationDelay: `${(columnIndex + 8 * 10) * staggerDelay}ms` },
        '&:nth-of-type(10)': { animationDelay: `${(columnIndex + 9 * 10) * staggerDelay}ms` },
      }),
      
      // Hover animations
      ...(hoverAnimation && {
        transition: 'all 0.3s ease-out',
        '&:hover': {
          transform: 'translateY(-8px) scale(1.02)',
          boxShadow: theme?.shadows?.[8] || '0px 8px 25px rgba(0, 0, 0, 0.15)',
          zIndex: 1,
        },
      }),
    },
  };
}); 